import { useQuery } from '@apollo/client'
import { Grid, Stack } from '@mantine/core'
import { Breadcrumbs } from 'components/Breadcrumbs'
import { FullPageError } from 'components/FullPageComponents'
import { FullContentLoader } from 'components/Loading'
import { useStore } from 'store'
import { useMediaQuery, useVisiblePolling } from 'util/hooks'
import { FutureShifts } from './components/FutureShifts'
import { RecentQuotes } from './components/RecentQuotes'
import { ShortcutCards } from './components/ShortcutCards'
import { TransactionCard } from './components/TransactionCard'
import { WantedList } from './components/WantedList'
import { DASHBOARD_DATA_QUERY } from './queries'
import { DashboardDataQueryReturns } from './types.graphql'
import { createStyles } from '@mantine/emotion'
import { JoinableShiftChats } from 'modules/chat'
import { PlanningAvailabilityBanner } from 'modules/schedules/components/PlanningAvailabilityBanner'

// The dashboard shows live data, so it polls often. Only a visible tab polls.
const DASHBOARD_POLL_MS = 10_000

const breadCrumbItems = [{ label: 'Hjem', path: '/dashboard' }]

export const Dashboard = () => {
  const { classes } = useStyles()
  const mediaQuery = useMediaQuery('(min-width: 1708px)')
  const user = useStore(state => state.user)!

  const { data, loading, error, startPolling, stopPolling, refetch } =
    useQuery<DashboardDataQueryReturns>(DASHBOARD_DATA_QUERY)
  useVisiblePolling({ startPolling, stopPolling, refetch }, DASHBOARD_POLL_MS)

  if (error) return <FullPageError />

  if (loading || data === undefined) return <FullContentLoader />

  const {
    dashboardData: {
      wantedList,
      lastQuotes,
      sociOrderSession,
      showNewbies,
      showStockMarketShortcut,
      showFeedback,
    },
  } = data
  return (
    <Stack gap="md" justify={'flex-start'} className={classes.wrapper}>
      <Breadcrumbs items={breadCrumbItems} />
      <PlanningAvailabilityBanner />
      <JoinableShiftChats maxRows={2} />
      <ShortcutCards
        sociOrderSession={!!sociOrderSession}
        showNewbies={showNewbies}
        showStockMarketShortcut={showStockMarketShortcut}
        showFeedback={showFeedback}
      />
      {wantedList.length >= 1 && <WantedList users={wantedList} />}
      <Grid justify={'space-between'}>
        <Grid.Col span={{ sm: 6, lg: mediaQuery ? 5 : 6 }}>
          <Stack gap="lg">
            <FutureShifts />
            <TransactionCard
              activities={user.lastTransactions}
              showEconomyLink
            />
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ sm: 6, lg: mediaQuery ? 5 : 6 }}>
          <RecentQuotes quotes={lastQuotes} />
        </Grid.Col>
      </Grid>
    </Stack>
  )
}

const useStyles = createStyles((_theme, _, u) => ({
  wrapper: {
    width: '100%',
    maxWidth: '1600px',

    [u.smallerThan('xl')]: {
      padding: 0,
    },
  },
}))
